import React from 'react';

/**
 * Composant pour afficher les graphiques de progression
 */
const ProgressionCharts = ({ progression }) => {
  
  // Calculer les données pour le graphique en cercle
  const getCircularProgressData = () => {
    if (!progression?.matieres || progression.matieres.length === 0) {
      return { completed: 0, inProgress: 0, notStarted: 100 };
    }

    const total = progression.matieres.length;
    const completed = progression.matieres.filter(m => m.statut === 'termine').length;
    const inProgress = progression.matieres.filter(m => m.statut === 'en_cours').length;
    const notStarted = total - completed - inProgress;

    return {
      completed: Math.round((completed / total) * 100),
      inProgress: Math.round((inProgress / total) * 100),
      notStarted: Math.round((notStarted / total) * 100)
    };
  };

  // Calculer le pourcentage global de progression
  const getGlobalProgressPercentage = () => {
    if (!progression?.matieres || progression.matieres.length === 0) return 0;

    let totalItems = 0;
    let completedItems = 0;

    progression.matieres.forEach(matiere => {
      totalItems += matiere.nombre_cours_total + matiere.nombre_exercices_total;
      completedItems += matiere.nombre_cours_termines + matiere.nombre_exercices_termines;
    });

    return totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;
  };

  // Obtenir les statistiques détaillées
  const getDetailedStats = () => {
    if (!progression?.matieres) {
      return {
        totalCours: 0,
        coursTermines: 0,
        totalExercices: 0,
        exercicesTermines: 0,
        totalLecons: 0,
        leconsTerminees: 0
      };
    }

    return progression.matieres.reduce((stats, matiere) => ({
      totalCours: stats.totalCours + matiere.nombre_cours_total,
      coursTermines: stats.coursTermines + matiere.nombre_cours_termines,
      totalExercices: stats.totalExercices + matiere.nombre_exercices_total,
      exercicesTermines: stats.exercicesTermines + matiere.nombre_exercices_termines,
      totalLecons: stats.totalLecons + matiere.nombre_lecons_total,
      leconsTerminees: stats.leconsTerminees + matiere.nombre_lecons_terminees
    }), {
      totalCours: 0,
      coursTermines: 0,
      totalExercices: 0,
      exercicesTermines: 0,
      totalLecons: 0,
      leconsTerminees: 0
    });
  };

  const circularData = getCircularProgressData();
  const globalProgress = getGlobalProgressPercentage();
  const detailedStats = getDetailedStats();

  return (
    <div className="row">
      {/* Progression globale circulaire */}
      <div className="col-md-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-body text-center">
            <h6 className="card-title text-muted mb-3">Progression Globale</h6>
            <div className="position-relative d-inline-block">
              <svg width="120" height="120" className="circular-progress">
                <circle
                  cx="60"
                  cy="60"
                  r="54"
                  fill="none"
                  stroke="#e9ecef"
                  strokeWidth="12"
                />
                <circle
                  cx="60"
                  cy="60"
                  r="54"
                  fill="none"
                  stroke="#0d6efd"
                  strokeWidth="12"
                  strokeLinecap="round"
                  strokeDasharray={`${globalProgress * 3.39} 339.292`}
                  strokeDashoffset="84.823"
                  transform="rotate(-90 60 60)"
                  style={{
                    transition: 'stroke-dasharray 0.5s ease-in-out'
                  }}
                />
              </svg>
              <div className="position-absolute top-50 start-50 translate-middle">
                <div className="fs-4 fw-bold text-primary">{globalProgress}%</div>
              </div>
            </div>
            <div className="mt-3">
              <small className="text-muted">Cours et exercices terminés</small>
            </div>
          </div>
        </div>
      </div>

      {/* Répartition par statut */}
      <div className="col-md-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-body">
            <h6 className="card-title text-muted mb-3">Répartition par Statut</h6>
            
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-success">Terminées</small>
                <small className="fw-bold text-success">{circularData.completed}%</small>
              </div>
              <div className="progress mb-2" style={{ height: '8px' }}>
                <div 
                  className="progress-bar bg-success" 
                  style={{ width: `${circularData.completed}%` }}
                ></div>
              </div>
            </div>

            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-warning">En cours</small>
                <small className="fw-bold text-warning">{circularData.inProgress}%</small>
              </div>
              <div className="progress mb-2" style={{ height: '8px' }}>
                <div 
                  className="progress-bar bg-warning" 
                  style={{ width: `${circularData.inProgress}%` }}
                ></div>
              </div>
            </div>

            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <small className="text-light">Non commencées</small>
                <small className="fw-bold text-muted">{circularData.notStarted}%</small>
              </div>
              <div className="progress mb-2" style={{ height: '8px' }}>
                <div 
                  className="progress-bar bg-light" 
                  style={{ width: `${circularData.notStarted}%` }}
                ></div>
              </div>
            </div>

            <hr className="my-3" />
            
            <div className="text-center">
              <small className="text-muted">
                Total : {progression?.matieres?.length || 0} matières
              </small>
            </div>
          </div>
        </div>
      </div>

      {/* Détails par type d'activité */}
      <div className="col-md-4">
        <div className="card border-0 shadow-sm h-100">
          <div className="card-body">
            <h6 className="card-title text-muted mb-3">Détails par Activité</h6>
            
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex align-items-center">
                  <i className="bi bi-book text-info me-2"></i>
                  <span className="small">Leçons</span>
                </div>
                <span className="badge bg-info">
                  {detailedStats.leconsTerminees}/{detailedStats.totalLecons}
                </span>
              </div>
              <div className="progress" style={{ height: '6px' }}>
                <div 
                  className="progress-bar bg-info" 
                  style={{ 
                    width: `${detailedStats.totalLecons > 0 
                      ? (detailedStats.leconsTerminees / detailedStats.totalLecons) * 100 
                      : 0}%` 
                  }}
                ></div>
              </div>
            </div>

            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex align-items-center">
                  <i className="bi bi-play-circle text-primary me-2"></i>
                  <span className="small">Cours</span>
                </div>
                <span className="badge bg-primary">
                  {detailedStats.coursTermines}/{detailedStats.totalCours}
                </span>
              </div>
              <div className="progress" style={{ height: '6px' }}>
                <div 
                  className="progress-bar bg-primary" 
                  style={{ 
                    width: `${detailedStats.totalCours > 0 
                      ? (detailedStats.coursTermines / detailedStats.totalCours) * 100 
                      : 0}%` 
                  }}
                ></div>
              </div>
            </div>

            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div className="d-flex align-items-center">
                  <i className="bi bi-clipboard-check text-success me-2"></i>
                  <span className="small">Exercices</span>
                </div>
                <span className="badge bg-success">
                  {detailedStats.exercicesTermines}/{detailedStats.totalExercices}
                </span>
              </div>
              <div className="progress" style={{ height: '6px' }}>
                <div 
                  className="progress-bar bg-success" 
                  style={{ 
                    width: `${detailedStats.totalExercices > 0 
                      ? (detailedStats.exercicesTermines / detailedStats.totalExercices) * 100 
                      : 0}%` 
                  }}
                ></div>
              </div>
            </div>

            <hr className="my-3" />
            
            <div className="text-center">
              <small className="text-muted">
                Progression détaillée par type
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressionCharts;
